﻿import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AxiosError } from 'axios';
import { Loader2 } from 'lucide-react';
import type { ApiError } from '@pms/shared';
import { subscriptionService, type SubscriptionStatus } from '@/services/subscription.service';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';

function errMsg(e: unknown): string {
  return (e as AxiosError<ApiError>).response?.data?.error?.message ?? 'Something went wrong';
}

interface FormValues { name: string; color: string; display_order: number; is_default: boolean }

interface Props {
  open: boolean;
  mode: 'create' | 'edit';
  status?: SubscriptionStatus;
  onClose: () => void;
  onSuccess: () => void;
}

export function StatusFormDialog({ open, mode, status, onClose, onSuccess }: Props) {
  const isEdit = mode === 'edit';
  const queryClient = useQueryClient();

  const { register, handleSubmit, reset, watch, setValue, formState: { errors } } = useForm<FormValues>({
    defaultValues: { name: '', color: '#6B7280', display_order: 0, is_default: false },
  });

  const color = watch('color');
  const isDefault = watch('is_default');

  useEffect(() => {
    if (open) {
      reset({
        name: status?.name ?? '',
        color: status?.color ?? '#6B7280',
        display_order: status?.display_order ?? 0,
        is_default: status?.is_default ?? false,
      });
    }
  }, [open, status, reset]);

  const mutation = useMutation({
    mutationFn: (v: FormValues) => {
      const input = { name: v.name.trim(), color: v.color, display_order: Number(v.display_order), is_default: v.is_default };
      return isEdit && status
        ? subscriptionService.updateStatus(status.id, input)
        : subscriptionService.createStatus(input);
    },
    onSuccess: () => {
      toast.success(isEdit ? 'Status updated' : 'Status created');
      void queryClient.invalidateQueries({ queryKey: ['subscriptions', 'statuses'] });
      onSuccess();
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-full max-w-md" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Status' : 'New Status'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit((v) => mutation.mutate(v))} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="st-name">Name *</Label>
            <Input id="st-name" {...register('name', { required: 'Name is required' })} placeholder="e.g. Active" />
            {errors.name && <p className="text-xs text-destructive">{errors.name.message}</p>}
          </div>
          <div className="space-y-1.5">
            <Label>Color</Label>
            <div className="flex gap-2 items-center">
              <input
                type="color"
                value={color}
                onChange={(e) => setValue('color', e.target.value)}
                className="h-9 w-14 rounded cursor-pointer border border-input bg-background p-0.5"
              />
              <Input value={color} onChange={(e) => setValue('color', e.target.value)} placeholder="#6B7280" className="flex-1" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="st-order">Display Order</Label>
            <Input id="st-order" type="number" {...register('display_order', { valueAsNumber: true })} placeholder="0" />
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              id="st-default"
              checked={isDefault}
              onChange={(e) => setValue('is_default', e.target.checked)}
            />
            <Label htmlFor="st-default" className="cursor-pointer font-normal">
              Set as default status
            </Label>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {isEdit ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
